/**
 * Сортировка таблиц по колонкам.
 *
 * Отдельным модулем без React: правила сравнения — это правила, а не
 * разметка, и проверять их надо без монтирования дерева. Заодно файл,
 * экспортирующий и компонент, и обычную функцию, Vite не обновляет
 * горячей заменой.
 */

/** Значение, по которому колонка сравнивается. */
export type SortValue = string | number | boolean | null | undefined;

export interface SortState {
  key: string;
  direction: 'asc' | 'desc';
}

/**
 * Следующее состояние после щелчка по заголовку.
 *
 * Три положения, а не два: по возрастанию, по убыванию и обратно к порядку
 * по умолчанию. Третье нужно потому, что порядок по умолчанию содержателен —
 * узлы приходят в порядке, заданном на карте, журнал в порядке событий, —
 * и вернуться к нему иначе можно было бы только перезагрузкой страницы.
 */
export function toggleSort(current: SortState | null, key: string): SortState | null {
  if (current === null || current.key !== key) {
    return { key, direction: 'asc' };
  }

  return current.direction === 'asc' ? { key, direction: 'desc' } : null;
}

/**
 * Сравнение двух значений одной колонки.
 *
 * Пустое значение всегда внизу — и при возрастании, и при убывании.
 * Это не мелочь: «адрес не задан» и «RTT не измерен» — не самые маленькие
 * значения, а их отсутствие, и всплывать наверх при сортировке по убыванию
 * они не должны. Инженер сортирует, чтобы увидеть худшее или лучшее,
 * а не список пропусков.
 */
export function compareValues(left: SortValue, right: SortValue): number {
  const leftEmpty = left === null || left === undefined || left === '';
  const rightEmpty = right === null || right === undefined || right === '';

  if (leftEmpty || rightEmpty) {
    return leftEmpty && rightEmpty ? 0 : leftEmpty ? 1 : -1;
  }

  if (typeof left === 'number' && typeof right === 'number') {
    return left - right;
  }

  if (typeof left === 'boolean' && typeof right === 'boolean') {
    // Истина выше лжи: «активен» и «работает» — то, ради чего сортируют.
    return left === right ? 0 : left ? -1 : 1;
  }

  // Русские названия в байтовом порядке сортируются мимо алфавита: «Ё»
  // оказывается после «я», а регистр разводит одинаковые слова.
  return String(left).localeCompare(String(right), 'ru', { numeric: true });
}

/**
 * Отсортированная копия. Исходный массив не трогается: он приходит
 * из кэша запросов, и сортировать его на месте значит менять чужие данные.
 *
 * Сортировка устойчива — так работает `Array.prototype.sort` начиная
 * с ES2019, — и это важно: строки с одинаковым значением в колонке
 * сохраняют порядок, в котором пришли, а не перемешиваются при каждом
 * повторном отображении.
 */
export function sortRows<T>(
  rows: readonly T[],
  sort: SortState | null,
  valueOf: (row: T, key: string) => SortValue,
): T[] {
  if (sort === null) {
    return [...rows];
  }

  const factor = sort.direction === 'asc' ? 1 : -1;

  return [...rows].sort((left, right) => {
    const compared = compareValues(valueOf(left, sort.key), valueOf(right, sort.key));

    // Пустые значения остаются внизу при любом направлении, поэтому
    // множитель к ним не применяется.
    const leftValue = valueOf(left, sort.key);
    const rightValue = valueOf(right, sort.key);
    const oneIsEmpty =
      isEmpty(leftValue) !== isEmpty(rightValue) || (isEmpty(leftValue) && isEmpty(rightValue));

    return oneIsEmpty ? compared : compared * factor;
  });
}

function isEmpty(value: SortValue): boolean {
  return value === null || value === undefined || value === '';
}
